import { Box, Grid } from '@mui/material'
import { LabelWithIndicator, Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { setSettingsSnapshot } from 'features/analysis/state/analysisSlice'
import { AnalysisSettings, AnalysisType, OperationType } from 'features/analysis/types'
import { AppFormGridColumn } from 'features/apps/ui/AppForm/appsFormStyle'
import { getEnrichedDatasetById } from 'features/builder/state/keplerGlSlice'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import ColumnsDropdown from '../../Commons/Form/ColumnsDropdown'
import AnalysisBase from '../AnalysisBase'
import AnalysisHeader from '../AnalysisHeader'
import { ColumnType } from '../FilterByColumn/FilterByColumnForm'
import AggregationFunctionSelector from '../IntersectAndAggregate/form/AggregationFunctionSelector'
import { generateCentroidQuery } from './queries'
import { CentroidSettings } from './types'

function CentroidForm({
  settings,
  onChange,
  onQueryChange
}: {
  settings: CentroidSettings
  onChange: (analysis: AnalysisSettings) => void
  onQueryChange: (query: string | undefined) => void
}) {
  const dispatch = useAppDispatch()
  const sourceDatasetId = useAppSelector((state) => state.analysis.sourceDatasetId) || ''
  const sourceDataset = useAppSelector((state) => getEnrichedDatasetById(state, sourceDatasetId as string))
  const [aggregationOperation, setAggregationOperation] = useState(settings?.aggregationOperation || '')
  const [aggregationField, setAggregationField] = useState(settings?.aggregationField || '')
  const [categorizeBy, setCategorizeBy] = useState(settings?.categorizeBy || '')
  const isAggregationFieldMandatory =
    (aggregationOperation as string) !== '' && aggregationOperation !== OperationType.COUNT

  useEffect(() => {
    if (
      (sourceDataset && !isAggregationFieldMandatory) ||
      (sourceDataset && isAggregationFieldMandatory && aggregationField)
    ) {
      onQueryChange(
        generateCentroidQuery({
          mainSource: sourceDataset,
          aggregationOperation: aggregationOperation as OperationType,
          aggregationField,
          categorizeBy
        })
      )
    } else if (sourceDataset && isAggregationFieldMandatory && !aggregationField) {
      onQueryChange(undefined)
    }
  }, [sourceDataset, aggregationOperation, aggregationField, categorizeBy, isAggregationFieldMandatory, onQueryChange])

  useEffect(() => {
    return () => {
      const settings: CentroidSettings = {
        mainSource: sourceDatasetId,
        aggregationOperation: aggregationOperation as OperationType,
        aggregationField,
        categorizeBy,
        analysisType: AnalysisType.Centroid
      }
      dispatch(setSettingsSnapshot(settings))
      onChange(settings)
    }
  }, [sourceDatasetId, aggregationOperation, aggregationField, categorizeBy, dispatch, onChange])

  return (
    <>
      <AnalysisHeader analysisType={AnalysisType.Centroid} />
      <Grid container spacing={4} mt={3} mb={2}>
        <AppFormGridColumn item sm={6}>
          <AnalysisBase analysisType={AnalysisType.Centroid} />
        </AppFormGridColumn>
        <Grid item sm={6}>
          <Box mb={1.5} display="flex" flexDirection="row" alignItems="center">
            <Typography variant="subtitle2">
              <LabelWithIndicator label={<FormattedMessage id="analysis.centroid.params" />} type="optional" />
            </Typography>
          </Box>

          <Box pt={0.5}>
            <ColumnsDropdown
              source={sourceDatasetId}
              placeholderId="analysis.centroid.selectColumn"
              labelId="analysis.centroid.categorize"
              column={categorizeBy}
              isOptional={true}
              excludeGeom={true}
              onChange={setCategorizeBy}
            />
          </Box>

          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              <FormattedMessage id="analysis.centroid.aggregationGroup" />
            </Typography>
            <Box mt={1.5}>
              <AggregationFunctionSelector
                operator={aggregationOperation}
                onChange={setAggregationOperation}
                isOptional={true}
              />
            </Box>
            <Box mt={1.5}>
              <ColumnsDropdown
                source={sourceDatasetId}
                labelId="analysis.centroid.aggregationField"
                placeholderId="analysis.centroid.selectColumn"
                isDisabled={(aggregationOperation as string) === '' || aggregationOperation === OperationType.COUNT}
                column={aggregationField}
                supportedColumnsTypes={[ColumnType.Number]}
                onChange={setAggregationField}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}

export default CentroidForm
